import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { systemService } from '../services/system/systemService';
import { useAuth } from './AuthContext';

interface NotificationCounts {
  system: {
    updates: number;
    alerts: number;
  };
  betaManagement: {
    pendingFeedback: number;
    newUsers: number;
  };
  deployment: {
    available: number;
  };
  revocations: {
    open: number;
  };
}

interface NotificationContextType {
  counts: NotificationCounts;
  refreshCounts: () => Promise<void>;
  clearCount: (path: string) => void;
  markSeen: (section: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [counts, setCounts] = useState<NotificationCounts>({
    system: {
      updates: 0,
      alerts: 0,
    },
    betaManagement: {
      pendingFeedback: 0,
      newUsers: 0,
    },
    deployment: {
      available: 0,
    },
    revocations: {
      open: 0,
    },
  });

  const refreshCounts = useCallback(async () => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (!token || !user || user.role !== 'admin') {
      return;
    }

    try {
      // System-Info und Notification-Counts parallel laden
      const [systemInfo, notificationCounts] = await Promise.all([
        systemService.getSystemInfo(),
        systemService.getAdminNotificationCounts(),
      ]);

      const deploymentUpdates = systemInfo.deployment.behind > 0 ? 1 : 0;

      let systemAlerts = 0;
      if (systemInfo.memory.percentage > 80) systemAlerts++;
      if (systemInfo.disk.percentage > 80) systemAlerts++;

      setCounts({
        system: {
          updates: 0,
          alerts: systemAlerts,
        },
        betaManagement: {
          pendingFeedback: notificationCounts.feedback,
          newUsers: notificationCounts.users,
        },
        deployment: {
          available: deploymentUpdates,
        },
        revocations: {
          open: notificationCounts.revocations,
        },
      });
    } catch (error) {
      // Silently fail for non-admin users
    }
  }, [user]);

  const clearCount = useCallback((path: string) => {
    const pathParts = path.split('.');
    setCounts(prev => {
      const newCounts = { ...prev };
      let current: any = newCounts;

      for (let i = 0; i < pathParts.length - 1; i++) {
        const key = pathParts[i];
        if (key === undefined) continue;
        current = current[key];
      }

      const lastKey = pathParts[pathParts.length - 1];
      if (lastKey !== undefined) {
        current[lastKey] = 0;
      }
      return newCounts;
    });
  }, []);

  const markSeen = useCallback(async (section: string) => {
    try {
      await systemService.markSectionSeen(section);
      // Nach dem Markieren sofort den lokalen Count auf 0 setzen
      const sectionMap: Record<string, string> = {
        feedback: 'betaManagement.pendingFeedback',
        users: 'betaManagement.newUsers',
      };
      const path = sectionMap[section];
      if (path) {
        clearCount(path);
      }
    } catch (error) {
      console.error('Fehler beim Markieren als gesehen:', error);
    }
  }, [clearCount]);

  useEffect(() => {
    refreshCounts();

    const interval = setInterval(refreshCounts, 60000);

    return () => clearInterval(interval);
  }, [refreshCounts]);

  const value = useMemo(() => ({ counts, refreshCounts, clearCount, markSeen }), [counts, refreshCounts, clearCount, markSeen]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
